/**
 * DEVELOPMENT / ADMIN VIEW — Learner Intelligence Debug (spec section 29).
 *
 * Admin-only. Not linked from any learner-facing surface. Lets an admin
 * inspect a learner's recent events, mastery, error patterns and cached
 * insight snapshots so we can verify that Makai's intelligence is
 * behaving. Never displays raw prompts or secrets.
 */
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { AdminShell } from "@/components/admin/admin-shell";
import { requireRole } from "@/lib/session";
import { supabase } from "@/integrations/supabase/client";
import { Brain, AlertTriangle } from "lucide-react";

export const Route = createFileRoute("/admin/learner-intelligence")({
  ssr: false,
  beforeLoad: () => requireRole("admin"),
  head: () => ({
    meta: [
      { title: "Learner Intelligence Debug — MAKAI" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: DebugPage,
});

function DebugPage() {
  const [learnerId, setLearnerId] = useState("");
  const [activeId, setActiveId] = useState<string | null>(null);

  return (
    <AdminShell
      title="Learner Intelligence — Debug"
      subtitle="Development / admin view · verify Makai's decisions for a learner."
    >
      <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900">
        <strong className="uppercase tracking-wide">Development / Admin view.</strong>{" "}
        Read-only. Do not share screenshots containing learner identifiers.
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          setActiveId(learnerId.trim() || null);
        }}
        className="mb-6 flex flex-wrap items-end gap-2"
      >
        <label className="flex flex-col text-xs text-muted-foreground">
          Learner ID (uuid)
          <input
            value={learnerId}
            onChange={(e) => setLearnerId(e.target.value)}
            placeholder="00000000-0000-0000-0000-000000000000"
            className="mt-1 w-96 rounded-lg border border-border bg-background px-3 py-1.5 text-sm"
          />
        </label>
        <button
          type="submit"
          className="rounded-lg bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground"
        >
          Load
        </button>
      </form>

      {activeId ? <LearnerDebugPanels learnerId={activeId} /> : (
        <div className="text-sm text-muted-foreground">
          Enter a learner ID to inspect their intelligence state.
        </div>
      )}
    </AdminShell>
  );
}

function LearnerDebugPanels({ learnerId }: { learnerId: string }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ["admin-learner-debug", learnerId],
    queryFn: async () => {
      const [events, mastery, patterns, readiness, insightsSnap] = await Promise.all([
        supabase
          .from("learner_events")
          .select("event_type, node_key, subject_id, occurred_at, payload, source")
          .eq("learner_id", learnerId)
          .order("occurred_at", { ascending: false })
          .limit(50),
        supabase
          .from("learner_mastery_nodes")
          .select("node_key, node_label, node_level, subject, mastery, confidence, updated_at")
          .eq("learner_id", learnerId)
          .order("updated_at", { ascending: false })
          .limit(50),
        supabase
          .from("learner_error_patterns")
          .select("node_key, error_kind, occurrences, updated_at")
          .eq("learner_id", learnerId)
          .order("occurrences", { ascending: false })
          .limit(20),
        supabase
          .from("learner_readiness")
          .select("node_key, node_label, readiness, blocking_node_key, updated_at")
          .eq("learner_id", learnerId)
          .limit(20),
        supabase
          .from("learner_insight_snapshots")
          .select("kind, headline, evidence, updated_at")
          .eq("learner_id", learnerId)
          .order("updated_at", { ascending: false })
          .limit(10),
      ]);
      return {
        events: events.data ?? [],
        mastery: mastery.data ?? [],
        patterns: patterns.data ?? [],
        readiness: readiness.data ?? [],
        snapshots: insightsSnap.data ?? [],
      };
    },
  });

  if (isLoading) return <div className="text-sm text-muted-foreground">Loading debug data…</div>;
  if (error) return <div className="text-sm text-destructive">Failed to load: {(error as Error).message}</div>;
  if (!data) return null;

  const blockers = (data.readiness as any[]).filter((r) => r.blocking_node_key);

  return (
    <div className="grid gap-4 xl:grid-cols-2">
      <Panel title="Blocking skills (from readiness)" icon={<AlertTriangle className="h-4 w-4" />}>
        {blockers.length === 0 ? (
          <Empty>None detected.</Empty>
        ) : (
          <ul className="space-y-1 text-sm">
            {blockers.map((r: any, i: number) => (
              <li key={i}>
                <span className="font-medium">{r.node_label ?? r.node_key}</span>
                <span className="text-xs text-muted-foreground"> ← blocked by {r.blocking_node_key}</span>
              </li>
            ))}
          </ul>
        )}
      </Panel>

      <Panel title="Cached insight snapshots" icon={<Brain className="h-4 w-4" />}>
        <List rows={data.snapshots.map((s: any) => ({
          left: s.headline ?? s.kind,
          right: s.updated_at?.slice(0, 10) ?? "",
        }))} />

      </Panel>

      <Panel title="Skill / concept mastery (top 50)">
        <table className="w-full text-xs">
          <thead className="text-left text-muted-foreground">
            <tr>
              <th className="py-1">Node</th>
              <th>Level</th>
              <th>Mastery</th>
              <th>Confidence</th>
              <th>Updated</th>
            </tr>
          </thead>
          <tbody>
            {(data.mastery as any[]).map((m, i) => (
              <tr key={i} className="border-t border-border/50">
                <td className="py-1 pr-2">{m.node_label ?? m.node_key}</td>
                <td className="pr-2">{m.node_level ?? "—"}</td>
                <td className="pr-2">{(m.mastery ?? 0).toFixed(2)}</td>
                <td className="pr-2">{(m.confidence ?? 0).toFixed(2)}</td>
                <td className="pr-2">{m.updated_at?.slice(0, 10)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Panel>

      <Panel title="Detected error patterns">
        <List rows={(data.patterns as any[]).map((p) => ({
          left: `${p.node_key} · ${p.error_kind}`,
          right: `${p.occurrences}×`,
        }))} />
      </Panel>

      <Panel title="Recent learning events (last 50)">
        <table className="w-full text-xs">
          <thead className="text-left text-muted-foreground">
            <tr>
              <th className="py-1">Time</th>
              <th>Type</th>
              <th>Node</th>
              <th>Source</th>
            </tr>
          </thead>
          <tbody>
            {(data.events as any[]).map((e, i) => (
              <tr key={i} className="border-t border-border/50">
                <td className="py-1 pr-2">{e.occurred_at?.slice(0, 19).replace("T", " ")}</td>
                <td className="pr-2">{e.event_type}</td>
                <td className="pr-2">{e.node_key ?? "—"}</td>
                <td className="pr-2">{e.source ?? "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Panel>
    </div>
  );
}

function Panel({
  title,
  icon,
  children,
}: {
  title: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-2xl border border-border bg-card p-4 shadow-soft">
      <div className="mb-2 flex items-center gap-2">
        {icon}
        <h3 className="text-sm font-semibold">{title}</h3>
      </div>
      {children}
    </section>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return <div className="text-xs text-muted-foreground">{children}</div>;
}

function List({ rows }: { rows: { left: string; right: string }[] }) {
  if (rows.length === 0) return <Empty>No data.</Empty>;
  return (
    <ul className="divide-y divide-border/50 text-sm">
      {rows.map((r, i) => (
        <li key={i} className="flex items-center justify-between py-1">
          <span className="min-w-0 truncate pr-3">{r.left}</span>
          <span className="text-xs text-muted-foreground">{r.right}</span>
        </li>
      ))}
    </ul>
  );
}
